import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, Loader2, RefreshCw, AlertCircle } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(true);
  const { loginWithLogto } = useAuth();

  useEffect(() => {
    let isMounted = true;

    const performAutoRedirect = async () => {
      try {
        setError('');
        setIsRedirecting(true);
        await loginWithLogto();
      } catch (err: any) {
        if (isMounted) {
          setIsRedirecting(false);
          setError(err.message || 'Failed to connect to Logto authentication server.');
        }
      }
    };

    performAutoRedirect();

    return () => {
      isMounted = false;
    };
  }, [loginWithLogto]);

  const handleRetry = async () => {
    try {
      setError('');
      setIsRedirecting(true);
      await loginWithLogto();
    } catch (err: any) {
      setIsRedirecting(false);
      setError(err.message || 'Logto OIDC authentication failed.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 relative overflow-hidden font-sans text-slate-100">
      {/* Background Gradients */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>

      <div className="w-full max-w-md bg-slate-900/90 backdrop-blur-xl p-8 rounded-3xl shadow-2xl relative z-10 border border-slate-800/80 text-center space-y-6">
        <div className="inline-flex p-3 rounded-2xl bg-gradient-to-tr from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 shadow-lg shadow-cyan-500/10">
          <ShieldCheck className="w-10 h-10" />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center justify-center gap-2">
            SensiAvera

          </h1>
          <p className="text-xs text-slate-400 mt-1">Enterprise Document Intelligence Gateway</p>
        </div>

        {isRedirecting ? (
          <div className="py-6 space-y-4">
            <div className="flex justify-center">
              <Loader2 className="w-9 h-9 text-cyan-400 animate-spin" />
            </div>
            <p className="text-sm font-medium text-slate-300 animate-pulse">
              Redirecting to Logto Identity Provider...
            </p>
            <p className="text-[11px] text-slate-500">
              Securing OIDC PKCE session & verifying Super Admin / User roles
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="p-4 rounded-2xl bg-red-950/60 border border-red-500/40 text-red-300 text-xs text-left space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-red-200">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  Logto Connection Notice
                </div>
                <div>{error}</div>
              </div>
            )}

            <button
              onClick={handleRetry}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-cyan-500/20 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Log In via Logto SSO</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
